import { useState, useEffect, useRef, useCallback } from "react";
import UseAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { useSIP } from "../context/SIPContext";

const TABS = [
    { key: "department", label: "Department", icon: "🏢" },
    { key: "agent", label: "Agent", icon: "🎧" },
    { key: "phone", label: "Mobile Number", icon: "📱" },
];

export default function CallTransferModal({ isOpen, onClose, callData, inline = false }) {
    const [activeTab, setActiveTab] = useState("department");
    const [departments, setDepartments] = useState([]);
    const [agents, setAgents] = useState([]);
    const [deptSearch, setDeptSearch] = useState("");
    const [agentSearch, setAgentSearch] = useState("");
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [phoneNumber, setPhoneNumber] = useState("");

    // drag state (only used in modal mode)
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const dragging = useRef(false);
    const dragOrigin = useRef({ x: 0, y: 0 });

    const searchRef = useRef(null);
    const axiosprivate = UseAxiosPrivate();
    const { auth } = useAuth();
    const user_id = auth?.user_details?.parent;
    const { sendDTMF } = useSIP();

    const onMouseMove = useCallback((e) => {
        if (!dragging.current) return;
        setPos({
            x: e.clientX - dragOrigin.current.x,
            y: e.clientY - dragOrigin.current.y,
        });
    }, []);

    const onMouseUp = useCallback(() => {
        dragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
    }, [onMouseMove]);

    function onHeaderMouseDown(e) {
        dragging.current = true;
        dragOrigin.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    }

    useEffect(() => {
        if (!isOpen) return;

        setPos({ x: 0, y: 0 });
        setActiveTab("department");
        setSelectedDept(null);
        setSelectedAgent(null);
        setPhoneNumber(callData?.customerNumber || "");
        setDeptSearch("");
        setAgentSearch("");

        const controller = new AbortController();

        const fetchData = async () => {
            try {
                const deptRes = await axiosprivate.post(
                    "/contactcentre/get_departments/",
                    { user_id },
                    { signal: controller.signal }
                );
                setDepartments(deptRes.data?.queues || deptRes.data || []);
            } catch (err) {
                if (err.name !== "CanceledError") console.error("Dept fetch error:", err);
            }

            try {
                const agentRes = await axiosprivate.post(
                    "/contactcentre/get_agents/",
                    { user_id },
                    { signal: controller.signal }
                );
                setAgents(agentRes.data?.agents || agentRes.data || []);
            } catch (err) {
                if (err.name !== "CanceledError") console.error("Agent fetch error:", err);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [isOpen]);

    useEffect(() => {
        if (searchRef.current) searchRef.current.focus();
    }, [activeTab]);

    if (!isOpen) return null;

    const filteredDepts = departments.filter(d =>
        (d.name || "").toLowerCase().includes(deptSearch.toLowerCase())
    );
    const filteredAgents = agents.filter(a => {
        if (!agentSearch) return true;
        const query = agentSearch.toLowerCase();
        return (
            (a.user_profile__profile_name || "").toLowerCase().includes(query) ||
            (a.vsip__virtualUser || "").includes(agentSearch) ||
            (a.extension || "").includes(agentSearch) ||
            (a.phone_number || "").includes(agentSearch)
        );
    });

    const canTransfer =
        (activeTab === "department" && selectedDept) ||
        (activeTab === "agent" && selectedAgent) ||
        (activeTab === "phone" && phoneNumber.trim().length > 6);

    function buildDTMF() {
        if (activeTab === "department") return selectedDept?.id ? `*22${selectedDept.queue_id}` : null;
        if (activeTab === "agent") {
            if (selectedAgent?.call_mode === "mobile")
                return selectedAgent?.phone_number ? `*44${selectedAgent.phone_number}` : null;
            return selectedAgent?.vsip__virtualUser ? `*44${selectedAgent.vsip__virtualUser}` : null;
        }
        if (activeTab === "phone") return `*44${phoneNumber.trim()}`;
        return null;
    }

    function handleTransfer() {
        if (!canTransfer) return;
        const dtmf = buildDTMF();
        if (dtmf) sendDTMF(dtmf);
        onClose();
    }

    // ── Shared inner content (tabs + body + footer) ──────────────────────────
    const innerContent = (
        <>
            {/* Tabs */}
            <div className="flex border-b bg-white">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex-1 py-2 text-xs font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors
                            ${activeTab === tab.key
                                ? "border-blue-600 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"}`}
                    >
                        <span>{tab.icon}</span> {tab.label}
                    </button>
                ))}
            </div>

            {/* Body */}
            <div className="p-4 min-h-[260px]">
                {/* Department tab */}
                {activeTab === "department" && (
                    <>
                        <div className="relative mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z" />
                            </svg>
                            <input
                                ref={searchRef}
                                type="text"
                                value={deptSearch}
                                onChange={e => setDeptSearch(e.target.value)}
                                placeholder="Search departments…"
                                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                            {filteredDepts.length === 0 && (
                                <p className="text-center text-sm text-gray-400 py-6">No departments found</p>
                            )}
                            {filteredDepts.map(dept => (
                                <button
                                    key={dept.id}
                                    onClick={() => setSelectedDept(selectedDept?.id === dept.id ? null : dept)}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors border
                                        ${selectedDept?.id === dept.id
                                            ? "bg-blue-50 border-blue-400"
                                            : "border-transparent hover:bg-gray-50 hover:border-gray-200"}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                        {(dept.name || "??").slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800">{dept.name}</p>
                                        <p className="text-xs text-gray-500">{dept.queue_id}</p>
                                    </div>
                                    {selectedDept?.id === dept.id && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* Agent tab */}
                {activeTab === "agent" && (
                    <>
                        <div className="relative mb-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z" />
                            </svg>
                            <input
                                ref={searchRef}
                                type="text"
                                value={agentSearch}
                                onChange={e => setAgentSearch(e.target.value)}
                                placeholder="Search by name, SIP or number…"
                                className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                            {filteredAgents.length === 0 && (
                                <p className="text-center text-sm text-gray-400 py-6">No available agents</p>
                            )}
                            {filteredAgents.map(agent => (
                                <button
                                    key={agent.id}
                                    onClick={() => setSelectedAgent(selectedAgent?.id === agent.id ? null : agent)}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors border
                                        ${selectedAgent?.id === agent.id
                                            ? "bg-blue-50 border-blue-400"
                                            : "border-transparent hover:bg-gray-50 hover:border-gray-200"}`}
                                >
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                        {(agent.user_profile__profile_name || agent.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800">{agent.user_profile__profile_name}</p>
                                        <p className="text-xs text-gray-500">
                                            {agent.call_mode === "mobile"
                                                ? <>📱 {agent.phone_number || "—"}</>
                                                : <>🖥️ {agent.vsip__virtualUser || "—"}</>}
                                        </p>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0
                                        ${agent.status === "available" ? "bg-blue-100 text-blue-700" :
                                            agent.status === "busy" ? "bg-orange-100 text-orange-700" :
                                                "bg-gray-100 text-gray-500"}`}>
                                        {agent.status}
                                    </span>
                                    {selectedAgent?.id === agent.id && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>
                    </>
                )}

                {/* Phone tab */}
                {activeTab === "phone" && (
                    <div className="pt-1">
                        <label className="block text-xs text-gray-600 font-medium mb-1.5">Customer mobile number</label>
                        <input
                            ref={searchRef}
                            type="tel"
                            value={phoneNumber}
                            onChange={e => setPhoneNumber(e.target.value)}
                            placeholder="10-digit mobile number"
                            className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {callData?.customerNumber && callData.customerNumber !== callData?.caller && (
                            <p className="mt-1.5 text-[11px] text-gray-400">
                                Auto-filled with the customer's actual number (call ID showed {callData.caller}).
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-4 py-3 border-t bg-gray-50 rounded-b-xl">
                <button
                    onClick={onClose}
                    className="px-4 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                >
                    Cancel
                </button>
                <button
                    onClick={handleTransfer}
                    disabled={!canTransfer}
                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg text-white transition-colors
                        ${canTransfer ? "bg-blue-600 hover:bg-blue-500 cursor-pointer" : "bg-blue-300 cursor-not-allowed"}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                    </svg>
                    Transfer
                </button>
            </div>
        </>
    );

    // ── Inline mode: render inside the parent popup ───────────────────────────
    if (inline) {
        return (
            <div className="bg-white overflow-hidden select-none animate-growFromIcon">
                {/* Inline header */}
                <div className="flex items-center justify-between px-4 py-2.5 border-b bg-gray-50">
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                            </svg>
                        </button>
                        <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                            </svg>
                            Transfer Call
                        </h2>
                    </div>
                    {callData?.caller && (
                        <span className="text-xs text-gray-500 font-medium">{callData.caller}</span>
                    )}
                </div>
                {innerContent}
            </div>
        );
    }

    // ── Modal mode: full-screen overlay (default) ─────────────────────────────
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm pointer-events-none">
            <div
                className="bg-white rounded-xl shadow-2xl w-[420px] overflow-hidden pointer-events-auto select-none animate-growFromIcon"
                style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
            >
                {/* Modal header — drag handle */}
                <div
                    onMouseDown={onHeaderMouseDown}
                    className="flex items-center justify-between px-4 py-2.5 border-b cursor-move bg-gray-50 rounded-t-xl"
                >
                    <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                        </svg>
                        Transfer Call
                        <span className="ml-1.5 text-[10px] text-gray-400 font-normal flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
                            </svg>
                            drag to move
                        </span>
                    </h2>
                    <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-200"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                {innerContent}
            </div>
        </div>
    );
}
